import React, { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_HOLD_MS = 1200;

export interface HoldToConfirmButtonProps {
  /** 모달·행이 바뀌면 게이지를 초기화하기 위한 키 */
  actionKey: string;
  holdDurationMs?: number;
  onComplete: () => void;
  children: React.ReactNode;
  className?: string;
  variant?: 'danger' | 'primary';
  disabled?: boolean;
  ariaLabel: string;
  title?: string;
}

/**
 * 길게 누르는 동안 왼쪽→오른쪽으로 게이지가 차면 onComplete를 한 번 호출합니다.
 */
const HoldToConfirmButton: React.FC<HoldToConfirmButtonProps> = ({
  actionKey,
  holdDurationMs = DEFAULT_HOLD_MS,
  onComplete,
  children,
  className = '',
  variant = 'primary',
  disabled = false,
  ariaLabel,
  title,
}) => {
  const [holdProgress, setHoldProgress] = useState(0);
  const holdStartRef = useRef<number | null>(null);
  const holdRafRef = useRef<number | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const cancelHold = useCallback(() => {
    if (holdRafRef.current != null) {
      cancelAnimationFrame(holdRafRef.current);
      holdRafRef.current = null;
    }
    holdStartRef.current = null;
    setHoldProgress(0);
  }, []);

  const holdLoop = useCallback(() => {
    const start = holdStartRef.current;
    if (start == null) return;
    const p = Math.min(1, (performance.now() - start) / holdDurationMs);
    setHoldProgress(p);
    if (p >= 1) {
      if (holdRafRef.current != null) {
        cancelAnimationFrame(holdRafRef.current);
        holdRafRef.current = null;
      }
      holdStartRef.current = null;
      const btn = btnRef.current;
      const pid = pointerIdRef.current;
      pointerIdRef.current = null;
      setHoldProgress(0);
      if (btn && pid != null) {
        try {
          btn.releasePointerCapture(pid);
        } catch {
          /* noop */
        }
      }
      onComplete();
      return;
    }
    holdRafRef.current = requestAnimationFrame(holdLoop);
  }, [holdDurationMs, onComplete]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      cancelHold();
      holdStartRef.current = performance.now();
      pointerIdRef.current = e.pointerId;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      holdRafRef.current = requestAnimationFrame(holdLoop);
    },
    [cancelHold, disabled, holdLoop]
  );

  const onPointerEnd = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const start = holdStartRef.current;
      if (start == null) return;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          /* noop */
        }
      }
      pointerIdRef.current = null;
      const p = Math.min(1, (performance.now() - start) / holdDurationMs);
      if (p < 1) cancelHold();
      e.stopPropagation();
    },
    [cancelHold, holdDurationMs]
  );

  useEffect(() => {
    return () => {
      if (holdRafRef.current != null) cancelAnimationFrame(holdRafRef.current);
    };
  }, []);

  useEffect(() => {
    cancelHold();
  }, [actionKey, cancelHold]);

  const variantClass = variant === 'danger' ? 'cp-hold-confirm-btn--danger' : 'cp-hold-confirm-btn--primary';

  return (
    <button
      ref={btnRef}
      type="button"
      disabled={disabled}
      className={`cp-hold-confirm-btn ${variantClass}${holdProgress > 0 ? ' is-holding' : ''}${className ? ` ${className}` : ''}`}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onPointerLeave={onPointerEnd}
      onContextMenu={(e) => e.preventDefault()}
      title={title ?? '버튼을 길게 누르면 실행됩니다'}
      aria-label={ariaLabel}
    >
      <span
        className="cp-hold-confirm-btn__bar"
        style={{ transform: `scaleX(${holdProgress})` }}
        aria-hidden
      />
      <span className="cp-hold-confirm-btn__label">{children}</span>
    </button>
  );
};

export default HoldToConfirmButton;
